import * as React from 'react';
import { Button, Dropdown, Flex, Icon, Text, FormContainer } from '@wyz/ui';
import { ICriteria } from '~/connect/types';
import { Trans, useI18n } from '~/common/context';
import { SwitchField } from '~/common/components';
import { useForm } from 'react-hook-form';
import { ROLES } from '~/common/helpers';
import { useAccessControl } from '~/common/hooks';

interface CriteriaSectionProps {
  criteria: ICriteria;
  onToggleVisibility: (id: string, visible: boolean) => void;
  onConfigure: (criteria: ICriteria) => void;
  onDelete: (id: string) => void;
  isUpdating: boolean;
  children: React.ReactNode;
}

export const CriteriaSection: React.FC<CriteriaSectionProps> = ({
  criteria,
  onToggleVisibility,
  onConfigure,
  onDelete,
  isUpdating,
  children,
}) => {
  const { t } = useI18n();
  const methods = useForm({
    defaultValues: {
      visible: criteria.visible,
    },
  });
  const acl = useAccessControl();

  React.useEffect(() => {
    methods.reset({ visible: criteria.visible });
  }, [criteria.visible, methods]);

  const handleVisibilityChange = (visible: boolean) => {
    onToggleVisibility(criteria.id, visible);
  };

  return (
    <div className='w-100 mb-3'>
      <FormContainer methods={methods} submitHandler={() => {}}>
        <div className='bg-neutral-grey-1 d-flex align-items-center justify-content-between px-4 py-3'>
          <Flex alignItems='center'>
            <Text color='info' heading='normal' weight='semi-bold'>
              {criteria.translatedName || criteria.name}
            </Text>
          </Flex>
          <Flex alignItems='center' className='gap-3'>
            <SwitchField
              name='visible'
              variant='DOUBLE'
              leftLabel={t('app.criteria.list.visibility.invisible')}
              rightLabel={t('app.criteria.list.visibility.visible')}
              formGroupClassName='mb-0'
              onChangeValue={handleVisibilityChange}
              disabled={isUpdating}
            />
            {!criteria.isReserved && (
              <Dropdown.Container>
                <Dropdown.Toggle
                  bsPrefix='toggle'
                  id={`criteria-dropdown-${criteria.id}`}
                  aria-label='Criteria actions'
                  as='div'
                >
                  <Button
                    id={`criteria-dropdown-button-${criteria.id}`}
                    variant='link'
                    size='xs'
                  >
                    <Icon icon='more' size='xxs' className='ms-2' />
                  </Button>
                </Dropdown.Toggle>
                <Dropdown.Menu className='dropdown-caret dropdown-menu-card dropdown-menu-end'>
                  {acl.isGranted([ROLES.ROLE_CRITERIA_UPDATE]) && (
                    <Dropdown.Item
                      id={`criteria-edit-${criteria.id}`}
                      onClick={() => onConfigure(criteria)}
                    >
                      <Icon icon='settings' width='1rem' className='me-2' />
                      <Trans i18nKey='app.criteria.list.actions.configure' />
                    </Dropdown.Item>
                  )}
                  {acl.isGranted([ROLES.ROLE_CRITERIA_DELETE]) && (
                    <Dropdown.Item
                      id={`criteria-delete-${criteria.id}`}
                      onClick={() => onDelete(criteria.id)}
                    >
                      <Icon icon='trash' width='1rem' className='me-2' />
                      <Trans i18nKey='app.common.buttons.delete' />
                    </Dropdown.Item>
                  )}
                </Dropdown.Menu>
              </Dropdown.Container>
            )}
          </Flex>
        </div>
      </FormContainer>
      <div className='bg-white p-4'>{children}</div>
    </div>
  );
};
